import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { normalizePartnerName, DUPLICATE_NAME_ERROR } from "@/lib/normalizeName";

export function useProveedores() {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["proveedores", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proveedores")
        .select("*, embarques(count)")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
  });
}

export function useCreateProveedor() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { nombre: string; nit?: string; pais?: string }) => {
      if (!orgId) throw new Error("Sin organización");

      // ── Capa 2: pre-check contra cache local ──────────────────────────
      const normalized = normalizePartnerName(values.nombre);
      if (!normalized) throw new Error("El nombre es obligatorio");

      const cached = queryClient.getQueryData<any[]>(["proveedores", orgId]) ?? [];
      const localDup = cached.find(
        (p) => normalizePartnerName(p?.nombre) === normalized,
      );
      if (localDup) {
        const err: any = new Error(DUPLICATE_NAME_ERROR);
        err.code = "DUPLICATE_NAME";
        err.existing = localDup;
        throw err;
      }

      // ── Insert defensivo ─────────────────────────────────────────────
      const { data, error } = await supabase
        .from("proveedores")
        .insert({
          org_id: orgId,
          nombre: values.nombre.trim(),
          nit: values.nit?.trim() || null,
          pais: values.pais?.trim() || null,
        })
        .select()
        .single();

      if (error) {
        // Postgres unique_violation (cuando la migración esté aplicada)
        if ((error as any).code === "23505") {
          const err: any = new Error(DUPLICATE_NAME_ERROR);
          err.code = "DUPLICATE_NAME";
          throw err;
        }
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proveedores", orgId] });
    },
  });
}

export function useDeleteProveedor() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("proveedores").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proveedores", orgId] });
    },
  });
}
